import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { useLanguageStore } from '../../store/languageStore'
import {
  decodeJwtPayload,
  getJwtSecondsLeft,
  getSessionWarningThresholdSeconds,
} from '../../utils/jwtPayload'
import SessionExpiryModal from './SessionExpiryModal'
import toast from 'react-hot-toast'
import { playFeedbackSound } from '../../services/feedbackSoundService'

const TICK_MS = 1000

export default function SessionExpiryMonitor() {
  const location = useLocation()
  const navigate = useNavigate()
  const { language } = useLanguageStore()
  const isRTL = language === 'ar'

  const token = useAuthStore((s) => s.token)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const setToken = useAuthStore((s) => s.setToken)
  const logout = useAuthStore((s) => s.logout)

  const tokenRef = useRef(token)
  tokenRef.current = token

  const [open, setOpen] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [renewing, setRenewing] = useState(false)
  const sessionWarningSoundPlayedRef = useRef(false)

  const onLogoutNow = useCallback(() => {
    setOpen(false)
    logout()
    if (location.pathname !== '/login') {
      navigate('/login', { replace: true })
    }
  }, [logout, navigate, location.pathname])

  const onRenew = useCallback(async () => {
    setRenewing(true)
    try {
      const { data } = await apiClient.post<{ token?: string }>(API_ENDPOINTS.auth.refresh, {})
      const newToken = data?.token
      if (!newToken) {
        throw new Error('No token in response')
      }
      setToken(newToken)
      setOpen(false)
      toast.success(isRTL ? 'تم تجديد الجلسة' : 'Session renewed')
    } catch {
      toast.error(isRTL ? 'تعذر تجديد الجلسة. سجّل الدخول مجدداً.' : 'Could not renew session. Please sign in again.')
      onLogoutNow()
    } finally {
      setRenewing(false)
    }
  }, [setToken, isRTL, onLogoutNow])

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setOpen(false)
      return
    }
    if (location.pathname === '/login' || location.pathname === '/audit-login') {
      setOpen(false)
      return
    }

    const tick = () => {
      const current = tokenRef.current
      if (!current) {
        setOpen(false)
        return
      }
      const payload = decodeJwtPayload(current)
      const threshold = getSessionWarningThresholdSeconds(payload)
      const left = getJwtSecondsLeft(current)
      if (left == null) {
        setOpen(false)
        return
      }
      setSecondsLeft(left)

      if (left <= 0) {
        setOpen(false)
        logout()
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true })
        }
        toast.error(isRTL ? 'انتهت الجلسة. سجّل الدخول مجدداً.' : 'Session expired. Please sign in again.')
        return
      }

      if (left <= threshold) {
        if (!sessionWarningSoundPlayedRef.current) {
          sessionWarningSoundPlayedRef.current = true
          playFeedbackSound('warning')
        }
        setOpen(true)
      } else {
        sessionWarningSoundPlayedRef.current = false
      }
    }

    tick()
    const id = window.setInterval(tick, TICK_MS)
    return () => window.clearInterval(id)
  }, [isAuthenticated, token, location.pathname, logout, navigate, isRTL])

  return (
    <SessionExpiryModal
      open={open}
      secondsLeft={secondsLeft}
      isRTL={isRTL}
      renewing={renewing}
      onRenew={onRenew}
      onLogoutNow={onLogoutNow}
    />
  )
}
